import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ShieldAlert, AlertTriangle, AlertCircle, Info, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_CONFIG = {
  critical: { color: "#dc2626", bg: "#fef2f2", icon: ShieldAlert, label: "Critical" },
  high: { color: "#ea580c", bg: "#fff7ed", icon: AlertTriangle, label: "High" },
  medium: { color: "#d97706", bg: "#fffbeb", icon: AlertCircle, label: "Medium" },
  low: { color: "#2563eb", bg: "#eff6ff", icon: Info, label: "Low" },
};

const ATTACK_LABELS = {
  prompt_injection: "Prompt Injection",
  meta_extraction: "Meta Extraction",
  response_leak: "Response Leak",
};

export const SecurityIncidentsPanel = ({ token }) => {
  const [incidents, setIncidents] = useState([]);
  const [total, setTotal] = useState(0);
  const [typeBreakdown, setTypeBreakdown] = useState({});
  const [severityBreakdown, setSeverityBreakdown] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState({ severity: "", attack_type: "" });

  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50", offset: "0" });
      if (filter.severity) params.set("severity", filter.severity);
      if (filter.attack_type) params.set("attack_type", filter.attack_type);
      const { data } = await axios.get(`${API}/api/admin/security-incidents?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIncidents(data.incidents || []);
      setTotal(data.total || 0);
      setTypeBreakdown(data.type_breakdown || {});
      setSeverityBreakdown(data.severity_breakdown || {});
    } catch (err) {
      console.error("Failed to fetch security incidents", err);
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { fetchIncidents(); }, [fetchIncidents]);

  const totalIncidents = Object.values(severityBreakdown).reduce((a, b) => a + b, 0);

  return (
    <Card className="border-red-200" data-testid="security-incidents-panel">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <ShieldAlert size={20} className="text-red-600" />
          <CardTitle className="text-lg text-[#08263e]">Security Incidents</CardTitle>
          {totalIncidents > 0 && (
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700" data-testid="incident-count-badge">
              {totalIncidents}
            </span>
          )}
        </div>
        <button onClick={fetchIncidents} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" data-testid="refresh-incidents-btn">
          <RefreshCw size={16} className={loading ? "animate-spin text-gray-400" : "text-gray-500"} />
        </button>
      </CardHeader>
      <CardContent>
        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {Object.entries(SEVERITY_CONFIG).map(([key, cfg]) => {
            const count = severityBreakdown[key] || 0;
            const Icon = cfg.icon;
            return (
              <div key={key} className="rounded-lg p-3 text-center" style={{ backgroundColor: cfg.bg }} data-testid={`severity-card-${key}`}>
                <Icon size={18} className="mx-auto mb-1" style={{ color: cfg.color }} />
                <div className="text-xl font-bold" style={{ color: cfg.color }}>{count}</div>
                <div className="text-xs text-gray-600">{cfg.label}</div>
              </div>
            );
          })}
        </div>

        {/* Attack Type Breakdown */}
        {Object.keys(typeBreakdown).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(typeBreakdown).map(([type, count]) => (
              <span key={type} className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700" data-testid={`type-badge-${type}`}>
                {ATTACK_LABELS[type] || type}: {count}
              </span>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2 mb-4">
          <select value={filter.severity} onChange={(e) => setFilter(f => ({ ...f, severity: e.target.value }))}
            className="text-xs border rounded-lg px-2 py-1.5 bg-white" data-testid="filter-severity">
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select value={filter.attack_type} onChange={(e) => setFilter(f => ({ ...f, attack_type: e.target.value }))}
            className="text-xs border rounded-lg px-2 py-1.5 bg-white" data-testid="filter-attack-type">
            <option value="">All Types</option>
            <option value="prompt_injection">Prompt Injection</option>
            <option value="meta_extraction">Meta Extraction</option>
            <option value="response_leak">Response Leak</option>
          </select>
        </div>

        {/* Incidents List */}
        {loading ? (
          <div className="text-center py-6 text-gray-400 text-sm">Loading...</div>
        ) : incidents.length === 0 ? (
          <div className="text-center py-8 text-gray-400" data-testid="no-incidents">
            <ShieldAlert size={32} className="mx-auto mb-2 text-green-400" />
            <p className="text-sm font-medium text-green-600">No security incidents detected</p>
            <p className="text-xs text-gray-400 mt-1">All systems operating normally</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {incidents.map((inc, idx) => {
              const cfg = SEVERITY_CONFIG[inc.severity] || SEVERITY_CONFIG.low;
              const Icon = cfg.icon;
              const isExpanded = expanded === idx;
              return (
                <div key={idx} className="border rounded-lg overflow-hidden" data-testid={`incident-row-${idx}`}>
                  <button onClick={() => setExpanded(isExpanded ? null : idx)}
                    className="w-full flex items-center gap-2 p-3 text-left hover:bg-gray-50 transition-colors">
                    <Icon size={16} style={{ color: cfg.color }} className="flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                          {cfg.label}
                        </span>
                        <span className="text-xs font-medium text-gray-700">
                          {ATTACK_LABELS[inc.attack_type] || inc.attack_type}
                        </span>
                        <span className="text-xs text-gray-400 ml-auto flex-shrink-0">
                          {new Date(inc.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{inc.user_message}</p>
                    </div>
                    {isExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                  </button>
                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-2 border-t bg-gray-50/50">
                      <div className="pt-2">
                        <p className="text-xs font-semibold text-gray-600 mb-1">User Message:</p>
                        <p className="text-xs text-gray-700 bg-white p-2 rounded border font-mono">{inc.user_message}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-1">Details:</p>
                        <p className="text-xs text-gray-600">{inc.details}</p>
                      </div>
                      {inc.blocked_response && (
                        <div>
                          <p className="text-xs font-semibold text-red-600 mb-1">Blocked LLM Response:</p>
                          <p className="text-xs text-red-700 bg-red-50 p-2 rounded border border-red-200 font-mono">{inc.blocked_response}</p>
                        </div>
                      )}
                      <div className="flex gap-4 text-xs text-gray-400">
                        {inc.session_id && <span>Session: {inc.session_id.slice(0, 12)}...</span>}
                        {inc.user_name && <span>User: {inc.user_name}</span>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {total > 50 && <p className="text-xs text-gray-400 text-center mt-2">Showing 50 of {total} incidents</p>}
      </CardContent>
    </Card>
  );
};
